// Imports
import { CheckIcon } from '@heroicons/react/20/solid';
import clsx from 'clsx';
import type React from 'react';

import { Logo } from '@/components/Logo';

// Types
type Step = 1 | 2 | 3;

type ProgressStepsProps = {
  step: Step;
};

const ProgressStep: React.FC<{ step: number; status: string; lastStep?: boolean }> = ({
  step,
  status,
  lastStep,
}) => {
  return (
    <li className={clsx('relative', !lastStep && 'pr-8 sm:pr-20')}>
      {!lastStep && (
        <div className="absolute inset-0 flex items-center" aria-hidden="true">
          <div
            className={clsx(
              'h-0.5 w-full',
              status === 'completed'
                ? 'bg-emerald-700 dark:bg-zinc-400'
                : 'bg-zinc-200 dark:bg-zinc-700',
            )}
          />
        </div>
      )}
      <div
        className={clsx(
          'relative flex h-6 w-6 items-center justify-center rounded-full',
          status === 'completed' && 'bg-emerald-700 dark:bg-zinc-200',
          status === 'current' &&
            'border-2 border-emerald-700 bg-white dark:border-white dark:bg-zinc-700',
          status === 'upcoming' && 'bg-white dark:bg-zinc-700',
        )}
      >
        {status === 'completed' ? (
          <CheckIcon className="h-4 w-4 text-white dark:text-zinc-900" />
        ) : (
          <span
            className={clsx(
              'h-2 w-2 rounded-full',
              status === 'current' ? 'bg-emerald-700 dark:bg-white' : 'bg-transparent',
            )}
            aria-hidden="true"
          ></span>
        )}
        <span className="sr-only">Step {step}</span>
      </div>
    </li>
  );
};

// Progress steps component
const ProgressSteps: React.FC<ProgressStepsProps> = ({ step }) => {
  // Helper function to get the color of a step based on the current step
  const getStepStatus = (stepNumber: Step) =>
    stepNumber < step ? 'completed' : stepNumber === step ? 'current' : 'upcoming';

  return (
    <>
      <a href="/">
        <Logo className="h-16 w-16 select-none" />
      </a>
      <nav aria-label="Progress">
        <ol
          role="list"
          className={clsx('flex list-none items-center justify-start p-0', step === 1 && 'hidden')}
        >
          <ProgressStep step={1} status={getStepStatus(1)} />
          <ProgressStep step={2} status={getStepStatus(2)} />
          <ProgressStep step={3} status={getStepStatus(3)} lastStep={true} />
        </ol>
      </nav>
    </>
  );
};

export default ProgressSteps;
